import React, { useEffect, useState } from 'react';
import { useParams, Navigate, Routes, Route } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Dashboard from './admin/Dashboard';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';

/**
 * Tenant Dashboard wrapper komponenta
 * Proverava da li korisnik ima pristup kompaniji preko slug-a
 * i prosleđuje slug Dashboard komponenti
 */
const TenantDashboard = () => {
  const { companySlug } = useParams();
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [accessData, setAccessData] = useState(null);
  
  useEffect(() => {
    const verifyAccess = async () => {
      try {
        // Prvo proveri da li je ovo web shop slug
        // Ako je path samo /:slug bez /dashboard, to je verovatno web shop
        const currentPath = window.location.pathname;
        if (!currentPath.includes('/dashboard') && !currentPath.includes('/') && currentPath.split('/').length === 2) {
          // Ovo je verovatno web shop slug, ne company slug
          // Redirect na PublicWebShop će se desiti automatski kroz rutiranje
          setLoading(false);
          setHasAccess(false);
          return;
        }
        
        const profile = JSON.parse(localStorage.getItem('profile') || '{}');
        const token = profile?.token || user?.token;
        
        if (!token) {
          setLoading(false);
          setHasAccess(false);
          return;
        }
        
        if (!companySlug) {
          console.error('❌ No company slug provided in URL!');
          console.error('  Current path:', window.location.pathname);
          console.error('  Expected format: /:companySlug/dashboard');
          setLoading(false);
          setHasAccess(false);
          return;
        }
        
        // Proveri da li korisnik ima pristup ovoj kompaniji
        const response = await axios.get(
          `${API_BASE_URL}/api/${companySlug}/verify-access`,
          { 
            headers: { 
              Authorization: `Bearer ${token}` 
            } 
          }
        );
        
        if (response.data.hasAccess) {
          setHasAccess(true);
          setAccessData(response.data);
          
          // Sačuvaj slug u localStorage za kasniju upotrebu
          localStorage.setItem('currentCompanySlug', companySlug);
          localStorage.setItem('currentCompanyId', response.data.companyId);
          localStorage.setItem('currentCompanyName', response.data.companyName);
        } else {
          setHasAccess(false);
        }
      } catch (error) {
        console.error('❌ Access verification failed:', error);
        console.error('Error details:', error.response?.data || error.message);
        console.error('Error status:', error.response?.status);
        console.error('Error config:', {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        });
        
        // Ako je 401 (Unauthorized), ne logout-uj korisnika odmah - možda je problem sa tokenom
        // Ako je 404 (Company not found), možda slug nije ispravan
        // Ako je 403 (Forbidden), korisnik nema pristup
        setHasAccess(false);
      } finally {
        setLoading(false);
      }
    };
    
    if (companySlug) {
      verifyAccess();
    } else {
      setLoading(false);
      setHasAccess(false);
    }
  }, [companySlug, user]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <LoadingSpinner />
        <p className="ml-4 text-white">Proveravam pristup...</p>
      </div>
    );
  }
  
  if (!hasAccess) {
    const userType = localStorage.getItem('userType');
    const redirectRole = userType === 'worker' ? 'worker' : 'company';
    return <Navigate to={`/auth?role=${redirectRole}&type=login`} replace />;
  }

  // Prosleđi slug Dashboard komponenti kroz Routes da bi nested rute radile
  // Use nested Routes to properly handle the path after /dashboard/
  return (
    <Routes>
      <Route path="dashboard/*" element={<Dashboard companySlug={companySlug} accessData={accessData} />} />
      <Route path="*" element={<Navigate to={`/${companySlug}/dashboard`} replace />} />
    </Routes>
  );
};

export default TenantDashboard;
